import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from '../Dashboard/index.module.css';
import { useDispatch, useSelector } from "react-redux";
import { addNewStaffMember, editStaffMember, getAllStaffMembers } from "../../../redux/features/staff/staffService";
import { showToast } from "../../../components/ToastMsg";
import BeatLoader from "react-spinners/BeatLoader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleXmark } from "@fortawesome/free-regular-svg-icons";
import Modal from "../../../components/Modal";
import Pagination from "../../../components/Pagination";
import NoResultsFound from "../../../components/NoResultsFound";
import SearchBar from "../../../components/SearchBar";
import TableSkeleton from "../../../components/Skeleton";
import Dropdown from "../../../components/Dropdown/Dropdown";
import { editUser } from "../../../redux/features/users/userService";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import SearchBarOnChange from "../../../components/SearchBarOnChange";

const Roles = [
    { id: '1', name: 'Admin' },
    { id: '2', name: 'Viewer' },
]

const Staff = () => {

    const dispatch = useDispatch();
    const roleDropdownRef = useRef(null);

    const { isStaffMembersLoading, pageCount, staffMembers } = useSelector((state) => state.staff.allStaffMembers);
    const { isStaffMemberCreating } = useSelector((state) => state.staff.newStaffMember);
    const { isEditingStaffMember } = useSelector((state) => state.staff.editedStaffMember);

    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        phone_number: '',
    });

    const [errorMessages, setErrorMessages] = useState({});
    const [disabledBtn, setBtnDisable] = useState(false);
    const [skip, setSkip] = useState(0);

    const limit = 10;  // Number of items per page
    const [isModalOpen, setModalOpen] = useState(false);
    const [selectedStaffMember, setSelectedStaffMember] = useState(null);
    const [initialStaffMember, setInitialStaffMember] = useState(null);
    const [editErrorMsgs, setEditErrorMsgs] = useState({
        name: '',
    });
    const [selectedRole, setSelectedRole] = useState({
        id: '',
        name: ''
    });

    const [disabledEditBtn, setEditBtnDisable] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const fetchAllStaffMembers = useCallback(() => {
        dispatch(getAllStaffMembers({
            skip: skip,
            limit,
            role_name: 'coordinator',
            search: searchQuery,
        }));
    }, [dispatch, skip, searchQuery]);

    useEffect(() => {
        fetchAllStaffMembers();
    }, [fetchAllStaffMembers]);

    const handleSelect = (option) => {
        setFormData({ ...formData, role_name: option.name });
        setErrorMessages({ ...errorMessages, role: "" });
        setSelectedRole({
            id: option.id,
            name: option.name
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        setErrorMessages((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    // Validate input
    const isValidStaffMember = () => {
        const errors = {};
        const trimmedName = formData.full_name.trim();
        if (!trimmedName) {
            errors.full_name = "Name is required.";
        }
        if (!formData.email) {
            errors.email = "Email is required.";
        } else if (!/^[\w.-]+@[a-zA-Z\d.-]+\.[a-zA-Z]{2,}$/.test(formData.email)) {
            errors.email = "Invalid email format.";
        }
        if (!formData.phone_number) {
            errors.phone_number = "Phone Number is required.";
        }
        if (Object.keys(errors).length > 0) {
            setErrorMessages(errors);
            return false;
        } else {
            return true;
        }
    };

    const insertNewStaffMember = async () => {
        if (isValidStaffMember()) {
            setBtnDisable(true);
            try {
                const response = await dispatch(addNewStaffMember(formData)).unwrap();
                setBtnDisable(false);
                setFormData({
                    full_name: '',
                    email: '',
                    phone_number: '',
                });
                if (roleDropdownRef.current) {
                    roleDropdownRef.current.clearSelection();
                }
                fetchAllStaffMembers();
                showToast('success', response.message);
            }
            catch (error) {
                setBtnDisable(false);
                showToast('error', error.message);
            };
        }
    };

    // Edit staffMember
    const handleSelectedStaffMemberChange = (e) => {
        const { name, value } = e.target;
        setErrorMsg(null);
        setSelectedStaffMember((prev) => ({
            ...prev,
            [name]: value,
        }));

        setEditErrorMsgs((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const isValidEditStaffMember = () => {
        const errors = {};
        const trimmedName = selectedStaffMember.full_name.trim();

        if (!trimmedName) {
            errors.full_name = "Name is required.";
        }
        if (!selectedStaffMember.phone_number) {
            errors.phone_number = "Phone Number is required.";
        }
        if (Object.keys(errors).length > 0) {
            setEditErrorMsgs(errors);
            return false;
        } else {
            return true;
        }
    };

    function getChangedValues(selectedData) {
        const changedValues = {};
        for (const key in selectedData) {
            if (
                key !== 'id' &&
                selectedData.hasOwnProperty(key) &&
                selectedData[key] !== initialStaffMember[key]
            ) {
                changedValues[key] = selectedData[key];
            }

            if (key === 'id') {
                changedValues[key] = selectedData[key];
            }
        }
        return changedValues;
    }

    const editStaffMemberDetails = async () => {
        if (isValidEditStaffMember(selectedStaffMember)) {
            const data = getChangedValues(selectedStaffMember);
            const hasChanges = Object.keys(data).some(key => key !== 'id');
            if (!hasChanges) {
                setErrorMsg('There are no changes to update.');
            } else {
                setEditBtnDisable(true);
                try {
                    const response = await dispatch(editStaffMember(data)).unwrap();
                    setEditBtnDisable(false);
                    setSelectedStaffMember(null);
                    setInitialStaffMember(null);
                    setModalOpen(false);
                    showToast('success', response.message);
                    fetchAllStaffMembers();
                }
                catch (error) {
                    setEditBtnDisable(false);
                    showToast('error', error.message);
                };
            }
        }
    };

    //Activate StaffMember
    const [isActivateModalOpen, setActivateModalOpen] = useState(false);
    const [isDeactivationModalOpen, setDeactivationModalOpen] = useState(false);
    const [selectedStaffMemberId, setSelectedStaffMemberId] = useState(null);

    const closeModal = () => setActivateModalOpen(false);
    const closDeactivationModal = () => setDeactivationModalOpen(false);

    const handleYes = async () => {
        setActivateModalOpen(false);
        try {
            const response = await dispatch(editUser({ id: selectedStaffMemberId, status: 'Active' })).unwrap();
            setSelectedStaffMemberId(null);
            setInitialStaffMember(null);
            showToast('success', response.message);
            fetchAllStaffMembers();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }
    const handleRejectYes = async () => {
        setDeactivationModalOpen(false);
        try {
            const response = await dispatch(editUser({ id: selectedStaffMemberId, status: 'Deactivated' })).unwrap();
            setSelectedStaffMemberId(null);
            setInitialStaffMember(null);
            showToast('success', response.message);
            fetchAllStaffMembers();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }

    const handlePageChange = (pageNumber) => {
        setSkip(pageNumber - 1);
    };

    const handleSearch = (query) => {
        setSearchQuery(query)
        setSkip(0);
    };

    return (
        <div className={`card-section`}>
            <div className={`main-card`}>
                <h1 className={`form-title`}>Add New Coordinator</h1>
                <div className={styles.formContainer}>
                    <form
                        className={styles.coordinatorForm}
                        noValidate
                    >
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Full Name<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='full_name'
                                        name='full_name'
                                        value={formData.full_name}
                                        onChange={handleInputChange}
                                        placeholder='Enter Name'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.full_name && (
                                        <p className={`text-danger`}>{errorMessages.full_name}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Email Address<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='email'
                                        name='email'
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder='Enter Email'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.email && (
                                        <p className={`text-danger`}>{errorMessages.email}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Phone Number<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='phone_number'
                                        name='phone_number'
                                        value={formData.phone_number}
                                        onChange={handleInputChange}
                                        placeholder='Enter Phone Number'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.phone_number && (
                                        <p className={`text-danger`}>{errorMessages.phone_number}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button
                            className={`btn-primary`}
                            type='button'
                            disabled={disabledBtn}
                            onClick={insertNewStaffMember}
                        >
                            {isStaffMemberCreating ? <BeatLoader color='#ffffff' /> : `Create`}
                        </button>
                    </form>
                </div>
            </div>
            <div className={`search-card`}>
                <div className={`search-card-header`}>
                    <h1 className={`form-title`}>Coordinators</h1>
                    <SearchBarOnChange
                        value={searchQuery}
                        onSearch={handleSearch}
                    />
                </div>
                <div className={`search-card-body`}>
                    {isStaffMembersLoading ?
                        <TableSkeleton columns={["Full Name", "Email", "Role", "Status", "Actions"]} rowCount={10} height={20} />
                        : staffMembers.length > 0 ?
                            <div className={`table-container`}>
                                <table className={`table`}>
                                    <thead className={`thead`}>
                                        <tr>
                                            <th className={`theadColumnOne`}>Full Name</th>
                                            <th>Email</th>
                                            <th>Phone Number</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`tbody`}>
                                        {staffMembers?.map((staffMember) => (
                                            <tr key={staffMember.id}>
                                                <td className={`tdataColumnOne`}>
                                                    {staffMember.full_name}
                                                </td>
                                                <td>{staffMember.email}</td>
                                                <td>{staffMember.phone_number}</td>
                                                <td>{staffMember.status}</td>
                                                <td className={`actionsBtn`}>
                                                    <button
                                                        className={staffMember.status === 'Active' ? `btn-inactive` : `btn-active`}
                                                        onClick={() => {
                                                            setSelectedStaffMemberId(staffMember.id);
                                                            staffMember.status === 'Active' ? setDeactivationModalOpen(true) : setActivateModalOpen(true);
                                                        }
                                                        }>{staffMember.status === 'Active' ? 'Deactivate' : 'Acitvate'}</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            :
                            <NoResultsFound />
                    }
                </div>
                <div className={`search-card-footer`}>
                    {pageCount > 0 && (
                        <div className={styles.paginationContainer}>
                            <Pagination
                                currentPage={skip + 1}
                                totalPages={pageCount}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </div>
            </div>
            {isModalOpen &&
                <div className={`modal-overlay`}>
                    <div className={`modal-card`}>
                        <div className={`modal-header`}>
                            <h4 className={`modal-title`}>Edit Staff Member</h4>
                            <FontAwesomeIcon icon={faXmark} onClick={() => setModalOpen(false)} size="lg" cursor="pointer" />
                        </div>
                        <div className={`modal-body`}>
                            <div className={styles.formContainer}>
                                <form
                                    className={styles.coordinatorForm}
                                    noValidate
                                >
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Name<span className={`required-star`}>*</span>
                                            </label>
                                            <input
                                                type='text'
                                                id='full_name'
                                                name='full_name'
                                                value={selectedStaffMember.full_name || ''}
                                                onChange={handleSelectedStaffMemberChange}
                                                placeholder='Enter StaffMember Name'
                                                className={`form-input-default`}
                                            />
                                            {editErrorMsgs.full_name && (
                                                <p className={`text-danger`}>{editErrorMsgs.full_name}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Email<span className={`required-star`}>*</span>
                                            </label>
                                            <input
                                                type='text'
                                                id='email'
                                                name='email'
                                                value={selectedStaffMember.email || ''}
                                                disabled
                                                placeholder='Enter Email'
                                                className={`form-input-default`}
                                            />
                                            {editErrorMsgs.email && (
                                                <p className={`text-danger`}>{editErrorMsgs.email}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Phone Number<span className={`required-star`}>*</span>
                                            </label>
                                            <input
                                                type='text'
                                                id='phone_number'
                                                name='phone_number'
                                                value={selectedStaffMember.phone_number || ''}
                                                onChange={handleSelectedStaffMemberChange}
                                                placeholder='Enter Phone Number'
                                                className={`form-input-default`}
                                            />
                                            {editErrorMsgs.phone_number && (
                                                <p className={`text-danger`}>{editErrorMsgs.phone_number}</p>
                                            )}
                                            {errorMsg && (
                                                <p className={`text-danger`}>{errorMsg}</p>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        className={`btn-primary`}
                                        type='button'
                                        disabled={disabledEditBtn}
                                        onClick={editStaffMemberDetails}
                                    >
                                        {isEditingStaffMember ? <BeatLoader color='#ffffff' /> : `Save`}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            }
            {isActivateModalOpen && (
                <Modal
                    title='Staff Member Activation'
                    onClose={closeModal}
                    onYes={handleYes}
                    isInput={false}
                    iconName={faCircleCheck}
                    bodyText={`Are you sure you want to activate the Staff Member?`}
                    colorType={`var(--green)`}
                />
            )}
            {isDeactivationModalOpen && (
                <Modal
                    title='Staff Member Deactivation'
                    onClose={closDeactivationModal}
                    onYes={handleRejectYes}
                    isInput={false}
                    iconName={faCircleXmark}
                    bodyText={`Are you sure you want to deactivate the Staff Member?`}
                    colorType={`var(--danger)`}
                />
            )}
        </div>
    );
};

export default Staff;